import sortBy from 'lodash/sortBy';

import Enums from '../types/api/Enums';
import { Categories, type UnitDefinition, Units } from '../types/api/generated-units';
import type { LookupInputOption } from '../types/app';
import { hasStringValue } from './equality';

/**
 * Finds the UnitDefinition from an arbitary string (or Enums.Unit) value.
 * Useful for finding the full definition of a unit from the wire or Formik
 * value.
 *
 * @returns a UnitDefinition, or null if the value is null or undefined or does
 *          not correspond to an existing UnitDefinition
 */
export const UnitFromString = (unitValue: string | null | undefined): UnitDefinition | null => {
  const enumUnit = Object.values(Enums.Unit).find((unit) => unit === unitValue);
  if (enumUnit === undefined) {
    return null;
  }
  return Units[enumUnit];
};

export const sortByGroupAndLabel = (
  groups: string[],
  opts: LookupInputOption[],
): LookupInputOption[] =>
  // Ensure that non-alphabetic labels (e.g. '%') sort last.
  sortBy(opts, (opt) => [groups.indexOf(opt.group ?? ''), opt.label.replace(/^\W+/, 'zzz')]);

/**
 * A precalculated list of LookupInputOptions for unit typeaheads, grouped by
 * category (in order of the generated category definitions) and sorted by unit
 * display value within the categories.
 */
export const UnitOptions: LookupInputOption[] = sortByGroupAndLabel(
  Object.keys(Categories),
  Object.values(Units).map((unit) => ({
    label: unit.label,
    sublabels: [unit.description],
    value: unit.value,
    group: unit.category.label,
  })),
);

/**
 * Given a currently selected unit, return a set of unit options for compatible
 * units (those that belong to the same category). If no unit definition is
 * found for the unit, return all available unit options.
 */
export const DependentUnitOptions = (unitValue: string | null | undefined): LookupInputOption[] => {
  const unitDef = UnitFromString(unitValue);
  if (unitDef === null) {
    return UnitOptions;
  }
  return UnitOptions.filter((opt) => opt.group === unitDef.category.label);
};

const METERS_PER_UNIT: Partial<Record<Enums.Unit, number>> = {
  [Enums.Unit.In]: 0.0254,
  [Enums.Unit.Ft]: 0.3048,
  [Enums.Unit.Yd]: 0.9144,
  [Enums.Unit.Lf]: 0.3048,
  [Enums.Unit.Mm]: 0.001,
  [Enums.Unit.Cm]: 0.01,
  [Enums.Unit.M]: 1,
  [Enums.Unit.Km]: 1000,
  [Enums.Unit.Mi]: 1609.344,
};

/**
 * Checks if the given unit is a length unit.
 * @param unit - The unit to check.
 * @returns True if the unit is a length unit, false otherwise.
 */
const isLengthUnit = (unit: Enums.Unit): boolean => {
  const unitDef = Units[unit];
  return unitDef?.category === Categories['Length'];
};

const METRIC_LENGTH_UNITS: Enums.Unit[] = [
  Enums.Unit.Mm,
  Enums.Unit.Cm,
  Enums.Unit.M,
  Enums.Unit.Km,
];

export const isMetricLengthUnit = (unit: Enums.Unit): boolean => METRIC_LENGTH_UNITS.includes(unit);

const EXPLICIT_CONVERSION_FACTORS: Partial<Record<string, number>> = {
  'gal:oz': 128,
  'oz:gal': 0.0078125,
  'gal:lb': 8.33,
  'lb:gal': 0.12004801921,
};

export const convertUnits = (
  amount: number,
  fromUnit: Enums.Unit,
  toUnit: Enums.Unit,
): number | null => {
  if (fromUnit === toUnit) {
    return amount;
  }

  const explicitFactor = EXPLICIT_CONVERSION_FACTORS[`${fromUnit}:${toUnit}`];
  if (explicitFactor !== undefined) {
    return amount * explicitFactor;
  }

  const fromDef = Units[fromUnit];
  const toDef = Units[toUnit];
  if (fromDef === undefined || toDef === undefined || fromDef.category !== toDef.category) {
    return null;
  }

  const { conversionFactor: fromFactor } = fromDef;
  const { conversionFactor: toFactor } = toDef;
  if (fromFactor === null || toFactor === null || toFactor === 0) {
    return null;
  }

  return (amount * fromFactor) / toFactor;
};

/**
 * Converts a value in meters to the specified unit.
 * @param meters - The value in meters.
 * @param targetUnit - The target unit to convert to.
 * @returns The converted value.
 */
export const convertMetersToUnit = (meters: number, targetUnit: Enums.Unit): number => {
  const metersPerUnit = METERS_PER_UNIT[targetUnit];
  if (metersPerUnit === undefined) {
    throw new Error(`Unsupported length unit: ${targetUnit}`);
  }
  return meters / metersPerUnit;
};

/**
 * Converts a value between two length units.
 * @param value - The value to convert.
 * @param fromUnit - The source unit.
 * @param toUnit - The target unit.
 * @returns The converted value.
 */
export const convertBetweenLengthUnits = (
  value: number,
  fromUnit: Enums.Unit,
  toUnit: Enums.Unit,
): number => {
  if (!isLengthUnit(fromUnit) || !isLengthUnit(toUnit)) {
    throw new Error(`Both units must be length units. Got: ${fromUnit} -> ${toUnit}`);
  }
  const fromMetersPerUnit = METERS_PER_UNIT[fromUnit];
  const toMetersPerUnit = METERS_PER_UNIT[toUnit];
  if (fromMetersPerUnit === undefined || toMetersPerUnit === undefined) {
    throw new Error(`Unsupported length unit conversion: ${fromUnit} -> ${toUnit}`);
  }
  const meters = value * fromMetersPerUnit;
  return meters / toMetersPerUnit;
};

/**
 * Given a unit value, return the human-readable label for that unit.
 * If the unit is not found, return an empty string.
 */
export const getUnitLabel = (
  unitValue: string | null | undefined,
  defaultLabel: string = '',
): string => {
  const unitDef = UnitFromString(unitValue);
  if (unitDef === null) {
    return defaultLabel;
  }
  return unitDef.label;
};

/**
 * Given a unit value, return a human-readable label that places that unit in a
 * denominator (prefixed with a slash).
 *
 * If the unit is not found, return an empty string.
 */
export const getUnitLabelWithSlash = (unitValue: string | null | undefined): string => {
  const label = getUnitLabel(unitValue);
  return hasStringValue(label) ? `/${label}` : '';
};

const SHORT_UNIT_LABELS: Partial<Record<Enums.Unit, string>> = {
  [Enums.Unit.CuYd]: 'CY',
};

export const getShortUnitLabel = (unit: Enums.Unit): string =>
  SHORT_UNIT_LABELS[unit] ?? getUnitLabel(unit);
